import { useContext, useEffect, useState } from 'react'
import { NVButton } from '@nv/basic-components'
import { Loader } from '@nv/basic-components'
import { TextField } from '@nv/basic-components'
import { LoginFlowContext, useVoterUserContext } from '@nv/contexts'
import { useQuery } from '@nv/hooks'
import { VoterAuthService } from '@nv/services'
import { useFormik } from 'formik'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import styles from './UserLoginOneKey.module.scss'
import '@djthoms/pretty-checkbox'

const initialValues = {
  username: '',
  password: '',
}

export function UserLoginOneKey({ findMemberNumber = false, helpEmail }: any) {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const navigate = useNavigate()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { setVoterUser } = useVoterUserContext()
  const query = useQuery()
  const accessCode = query.get('accessCode')

  const LOGIN_CONTEXT = useContext(LoginFlowContext)

  const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/

  const loginSchema = Yup.object().shape({
    username: Yup.string().required(t('form.validations.accessKeyRequired')),
    password: Yup.string().matches(dateRegex, t('form.validations.dobFormatValidation')).required(t('form.validations.dobRequired')),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        if (!executeRecaptcha) {
          //console.log('Execute recaptcha not yet available')
          return
        }
        const token = await executeRecaptcha('voter_login')

        const userInput = {
          username: values.username,
          password: values.password,
        }
        const resp = await VoterAuthService.loginOneKey(userInput.username, userInput.password, '', token)

        if (resp?.success) {
          if (resp.user) {
            setVoterUser(resp.user)
          }
          if (!resp.link) {
            if (resp.passwordResetRequired) {
              LOGIN_CONTEXT.setLoginFlow('Forgot Password')
            }
            navigate('/voting/voter-verify/select-verifier')
          }
          setError(false)
        } else {
          setError(true)
          // console.error(resp.error?.message)
          switch (resp.error?.message) {
            case 'Invalid user credentials':
              setErrorMessage(t('auth.login.errors.invalidCredentials'))
              break
            case 'session error: only one session per user':
              setErrorMessage(t('auth.login.errors.session'))
              break
            case 'Account disabled':
              setErrorMessage(t('auth.login.errors.accountDisabled'))
              break
            case 'Account locked.':
              setErrorMessage(t('auth.login.errors.accountLocked'))
              break

            case 'recaptcha error':
              setErrorMessage(t('auth.login.errors.recaptcha'))
              break
            case 'User has already voted.':
              setErrorMessage(t('auth.login.errors.alreadyVoted'))
              break
            case 'No active events.':
              setErrorMessage(t('auth.login.errors.noActiveEvents'))
              break
            default:
              setErrorMessage(t('form.validations.invalidCredentials'))
              break
          }
        }
      } catch (error) {
        setError(true)
      } finally {
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    if (errorMessage) {
      setErrorMessage('')
    }
  }, [formik.values.password])

  useEffect(() => {
    if (accessCode) {
      formik.setFieldValue('username', accessCode)
    }
  }, [accessCode])

  return (
    <div className={styles['container']}>
      <form
        className={styles['form']}
        onSubmit={(e) => {
          e.preventDefault()
          formik.submitForm()
        }}
      >
        <span className={styles['title']}>{t('pages.login')}</span>

        <div className={styles['form__section']}>
          <TextField
            classes={{ container: styles['username-input'] }}
            formik={formik}
            field='username'
            placeholder={t('form.labels.enterOneKey')}
            label={t('form.labels.oneKey')}
            isRequired={true}
            maxChars={20}
          />

          {findMemberNumber && (
            <a
              href={
                currentLanguage === 'en'
                  ? 'https://pipsc.ca/member-tools/find-your-membership-number'
                  : 'https://pipsc.ca/fr/outils-des-membres/numero-de-membre'
              }
              className={styles['membership']}
              target='_blank'
              rel='noopener noreferrer'
            >
              {t('auth.login.labels.username.helpLinkLabel')}
            </a>
          )}
        </div>

        <div className={styles['form__section']}>
          <TextField
            classes={{ container: styles['password-input'] }}
            formik={formik}
            field='password'
            // isPassword={true}
            label={t('form.labels.dateOfBirth')}
            placeholder={'YYYY-MM-DD'}
            isRequired={true}
          />
        </div>

        {error && (
          <span className={styles['errors']} role='alert'>
            {errorMessage}
          </span>
        )}

        {loading ? (
          <Loader />
        ) : (
          <NVButton
            classes={{ container: styles['button'], content: styles['content'] }}
            height={50}
            text={t('buttons.next')}
            onClick={() => formik.submitForm()}
            type='submit'
          />
        )}
      </form>
      {helpEmail && (
        <div className={styles['footer']}>
          {t('auth.help.needHelp')}{' '}
          <a target='_blank' rel='noreferrer' href={`mailto:${helpEmail}`}>
            {t('auth.help.contactUs')}
          </a>
        </div>
      )}
    </div>
  ) //
}
